import { ColyseusMatchConnection } from './ColyseusMatchConnection.js';

// V2 network-boot helper. Reads `?room=ABCD` (private rooms),
// `?quickplay=1` (V2.5 Quick Play), and the optional `?endpoint=...`
// override from the current URL and constructs a
// `ColyseusMatchConnection` ready for `connect(playerId)`. Returns null
// when the URL has neither a room nor a quickplay param, signalling
// "stay in local mode".
//
// V2.5 endpoint resolution priority (consumed by `resolveNetworkEndpoint`
// below; the parser receives the already-resolved fallback):
//
//   1. Explicit `?endpoint=ws://...` in the URL — overrides everything,
//      used for local-server testing against a deployed client.
//   2. `import.meta.env.VITE_NETWORK_ENDPOINT` — compile-time inlined by
//      Vite from `apps/client/.env.production` for the static-CDN deploy.
//   3. `defaultEndpointFromOrigin(originHref)` — derives `wss://<host>:2567`
//      from the page origin. The dev / GitHub-Pages-preview fallback.
//
// V2 spine UX:
//   - https://signalclash.example/                  → local-vs-bot
//   - https://signalclash.example/?room=ABCD        → join the room ABCD
//   - https://signalclash.example/?room=ABCD&endpoint=ws://localhost:2567
//                                                   → local-server testing
//
// Why parse here instead of inside ColyseusMatchConnection: the connection
// adapter is transport-only; URL parsing is a UX concern. Keeping the two
// separate means a future "Quick Play" matchmaker can construct the
// connection without touching `window.location`.

// V2 private-room boot. `roomCode` is set; the connection joins
// `signal_clash` with `filterBy(['room'])` matchmaker pairing.
export interface ParsedPrivateRoomBoot {
  kind: 'private';
  roomCode: string;
  endpoint: string;
}

// V2.5 Quick Play boot. `roomCode` is unset; the connection joins
// `signal_clash_quick` with no filterBy — Colyseus pairs the next two
// waiting clients regardless of options. The room handles bot-fill on
// timeout server-side.
export interface ParsedQuickPlayBoot {
  kind: 'quickplay';
  endpoint: string;
}

export type ParsedNetworkBoot = ParsedPrivateRoomBoot | ParsedQuickPlayBoot;

const DEFAULT_ROOM_PARAM = 'room';
const DEFAULT_ENDPOINT_PARAM = 'endpoint';
const DEFAULT_QUICKPLAY_PARAM = 'quickplay';


const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const ROOM_CODE_ALLOWED = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const ROOM_CODE_MIN_LEN = 4;
const ROOM_CODE_MAX_LEN = 6;

export function sanitizeRoomCode(raw: string): string {
  const upper = raw.toUpperCase();
  let out = '';
  for (const ch of upper) {
    if (ROOM_CODE_ALLOWED.includes(ch)) out += ch;
  }
  if (out.length < ROOM_CODE_MIN_LEN) return '';
  if (out.length > ROOM_CODE_MAX_LEN) out = out.slice(0, ROOM_CODE_MAX_LEN);
  return out;
}

export function mintRoomCode(length = 5): string {
  const n = Math.max(ROOM_CODE_MIN_LEN, Math.min(ROOM_CODE_MAX_LEN, Math.floor(length)));
  let out = '';
  for (let i = 0; i < n; i++) {
    const idx = Math.floor(Math.random() * ROOM_CODE_ALPHABET.length);
    out += ROOM_CODE_ALPHABET[idx]!;
  }
  return out;
}

export function parseNetworkBootFromUrl(
  href: string,
  fallbackEndpoint: string,
): ParsedNetworkBoot | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const endpointFromUrl = url.searchParams.get(DEFAULT_ENDPOINT_PARAM);
  const endpoint =
    endpointFromUrl !== null && endpointFromUrl.trim() !== ''
      ? endpointFromUrl
      : fallbackEndpoint;

  // ?room= takes precedence over ?quickplay= so a private-room URL
  // that accidentally also carries quickplay (e.g., a stale share)
  // still routes to the named room.
  const room = url.searchParams.get(DEFAULT_ROOM_PARAM);
  if (room !== null && room.trim() !== '') {
    const roomCode = sanitizeRoomCode(room);
    if (roomCode !== '') return { kind: 'private', roomCode, endpoint };
  }

  const quickplay = url.searchParams.get(DEFAULT_QUICKPLAY_PARAM);
  // Treat anything other than the literal "0" / empty / absent as "on".
  // The canonical value is "1" but a typoed "true" / "yes" still works.
  if (
    quickplay !== null &&
    quickplay.trim() !== '' &&
    quickplay.trim() !== '0' &&
    quickplay.trim().toLowerCase() !== 'false'
  ) {
    return { kind: 'quickplay', endpoint };
  }

  return null;
}

export function createNetworkConnection(boot: ParsedNetworkBoot): ColyseusMatchConnection {
  if (boot.kind === 'quickplay') {
    return new ColyseusMatchConnection({
      endpoint: boot.endpoint,
      roomType: 'signal_clash_quick',
    });
  }
  return new ColyseusMatchConnection({
    endpoint: boot.endpoint,
    roomCode: boot.roomCode,
  });
}

// V2.5 fallback resolver. Encodes the priority chain documented at the top
// of this file: env wins over origin-derived; the URL `?endpoint=` overlay
// is applied INSIDE `parseNetworkBootFromUrl` (because it lives on the URL,
// not in the fallback chain).
export interface NetworkEndpointFallback {
  // VITE_NETWORK_ENDPOINT — Vite inlines this at build time when set in
  // .env.production. Can be `undefined` / `null` / `''` in dev or when the
  // var is intentionally omitted; the resolver treats those as "absent"
  // and falls through.
  envEndpoint?: string | null | undefined;
  // The full page URL (from `window.location.href`). Used to derive the
  // origin-based endpoint when neither URL nor env is set.
  originHref: string;
}

export function resolveNetworkEndpoint(fallback: NetworkEndpointFallback): string {
  const env = fallback.envEndpoint;
  if (typeof env === 'string' && env.trim() !== '') return env.trim();
  return defaultEndpointFromOrigin(fallback.originHref);
}

// Default endpoint inferred from the page origin. https → wss, http → ws.
// Caller can override via the `?endpoint=` query param or by passing a
// fallback. The :2567 port matches the server's default in
// `apps/server/src/index.ts`; adjust together if either changes.
export function defaultEndpointFromOrigin(originHref: string): string {
  try {
    const url = new URL(originHref);
    const wsProto = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProto}//${url.hostname}:2567`;
  } catch {
    return 'ws://localhost:2567';
  }
}
